import { type VoidAsyncResult, createResolvedAsyncResult } from "@/cache/AsyncResult";
import type { ScenePublicApi } from "@/layers/Compositor";
import { type CheckedProps, LayerBase, type PropsChecker } from "@/layers/LayerBase";
import { getDrawLayers } from "@/layers/geojson/drawing/drawing-layer-utilities";
import { DrawRadius, Idle } from "@/layers/geojson/drawing/mapbox-draw-mode";
import type { AvailableDrawModeName, AvailableDrawModes } from "@/layers/geojson/drawing/mapbox-draw-mode/modeNames";
import {
  removeLayerGroup,
  removeSource,
  updateMapboxLayoutProperties,
  updateMapboxPaintProperties,
} from "@/layers/utility/mapbox-utils";
import {
  MapDrawingState,
  MapDrawingToolEventType,
  type MapDrawingToolEventUnion,
  type MapDrawingToolStateType,
  globalMapDrawingService,
} from "@/models/map-drawing-tools";
import { safeFmt } from "@/utility/safeFmt";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import type { DrawingLayer, GuiTimeZone } from "@mm/metx-workbench.meteomatics.com";
import type { FeatureCollection } from "geojson";
import type { DateTime } from "luxon";
import type { BaseActionObject, ResolveTypegenMeta, ServiceMap, State, Subscription, TypegenDisabled } from "xstate";
import { DRAWING_STYLES } from "./style";

const DRAW_MODE_TO_MAP_DRAWING_STATE: Record<AvailableDrawModeName, MapDrawingState> = {
  idle: MapDrawingState.IDLE,
  draw_radius: MapDrawingState.DRAW_RADIUS,
};

// @ts-ignore: TODO Error should go away, once DrawingLaer is part of LayerBase
export class DrawingLayerImpl extends LayerBase<DrawingLayer> {
  private mapboxDraw: MapboxDraw;
  private drawingModes: AvailableDrawModes;
  private subscription: Subscription = { unsubscribe: () => {} };
  private awaitingMapLoad = false;
  private pendingDrawings: FeatureCollection | undefined;

  constructor(
    id: number,
    props: DrawingLayer,
    scene: ScenePublicApi,
    timezone: GuiTimeZone,
    drawings: FeatureCollection,
  ) {
    super(id, props, scene, timezone);

    this.drawingModes = {
      ...MapboxDraw.modes,
      idle: Idle(),
      draw_radius: DrawRadius(),
    };

    this.mapboxDraw = new MapboxDraw({
      defaultMode: "idle",
      displayControlsDefault: false,
      modes: this.drawingModes,
      styles: DRAWING_STYLES,
      userProperties: true,
    });

    this.initializeDrawing(drawings);
  }

  private initializeDrawing(drawings: FeatureCollection) {
    const map = this.scene.getMapboxMap();

    this.addEventListeners();

    // mapbox-draw only attaches its layers and mouse listeners once `map.loaded()` is true and polls for that
    // from a timer. A map that keeps repainting (style marked dirty after every frame) is never "loaded"
    // between two frames, so the timer never succeeds and drawing silently does not work. Inside the `render`
    // event the dirty flags are clean, so attach the control from there instead.
    if (map.loaded()) {
      this.attachControl(drawings);
    } else {
      this.pendingDrawings = drawings;
      this.awaitingMapLoad = true;
      map.on("render", this.attachWhenMapLoaded);
    }
    // subscribing emits the current state right away, so the control (or the awaiting flag) has to be set up first
    this.subscription = globalMapDrawingService.subscribe(this.changeDrawingMode.bind(this));
  }

  private readonly attachWhenMapLoaded = () => {
    const map = this.scene.getMapboxMap();
    if (!map.loaded()) {
      return;
    }
    map.off("render", this.attachWhenMapLoaded);
    this.awaitingMapLoad = false;
    this.attachControl(this.pendingDrawings);
    this.pendingDrawings = undefined;
    // mode changes were ignored while the control was not attached yet
    this.changeDrawingMode(globalMapDrawingService.getSnapshot());
  };

  private attachControl(drawings: FeatureCollection | undefined) {
    const map = this.scene.getMapboxMap();

    // Typing seems to have issue on mapbox-gl-draw level: https://github.com/mapbox/mapbox-gl-draw/issues/1257
    // @ts-ignore
    map.addControl(this.mapboxDraw);

    if (drawings) {
      this.mapboxDraw.add(drawings);
    }
  }

  private addEventListeners(): void {
    const map = this.scene.getMapboxMap();
    map.on("draw.modechange", this.handleModeChange);
  }

  private handleModeChange = (e: { mode: string }) => {
    if (e.mode === "idle") {
      globalMapDrawingService.send({
        type: MapDrawingToolEventType.publish,
        data: this.mapboxDraw.getAll(),
        mapId: this.props.id_cartographicmap,
      });
    }
  };

  changeDrawingMode(
    state: State<
      unknown,
      MapDrawingToolEventUnion,
      any,
      MapDrawingToolStateType,
      ResolveTypegenMeta<TypegenDisabled, MapDrawingToolEventUnion, BaseActionObject, ServiceMap>
    >,
  ) {
    if (this.awaitingMapLoad) {
      return;
    }
    const availableModes = Object.keys(this.drawingModes) as AvailableDrawModeName[];
    const mode = availableModes.find((mode) => state.matches(DRAW_MODE_TO_MAP_DRAWING_STATE[mode]));
    if (mode) {
      if (this.mapboxDraw.getMode() !== mode) {
        this.mapboxDraw.changeMode(mode);
      }
    } else {
      if (this.mapboxDraw.getMode() !== "idle") {
        this.mapboxDraw.changeMode("idle");
      }
    }
  }

  humanReadableId(): string {
    return safeFmt`metx.DrawingLayer#${this.uid}`;
  }

  moveZIndex(_beforeLayerId?: string | undefined): void {
    // TODO: Check if the layer ordering works, without these lines of code.
    // const drawingLayerIds = getDrawLayers(this.scene.getMapboxMap()).reverse();
    // moveZIndex(this.scene.getMapboxMap(), drawingLayerIds, beforeLayerId);
  }

  // biome-ignore lint/suspicious/noEmptyBlockStatements: <explanation>
  beforeRender(): void {}

  // @ts-ignore: TODO Error should go away, once DrawingLayer is part of LayerBase
  checker(): PropsChecker<DrawingLayer, LayerBase<DrawingLayer>> {
    type CheckerProps = CheckedProps<DrawingLayer>;
    const checkLayerProp = (property: CheckerProps) => (prev: any, curr: any) => {
      const changed = prev[property] !== curr[property];
      if (changed) {
        const map = this.scene.getMapboxMap();
        this.setLayerProps({ [property]: curr[property] });
        switch (property) {
          case "show":
            updateMapboxLayoutProperties(map, getDrawLayers(map), {
              visibility: curr.show ? "visible" : "none",
            });
            break;
          case "opacity":
            updateMapboxPaintProperties(map, getDrawLayers(map), {
              "icon-opacity": curr.opacity,
              "text-opacity": curr.opacity,
            });
            break;
          case "calibrated":
          case "custom_options":
            break;

          default: {
            const _exhaustive: never = property;
            return _exhaustive;
          }
        }
      }

      return changed;
    };
    return {
      opacity: checkLayerProp("opacity"),
      calibrated: checkLayerProp("calibrated"),
      custom_options: checkLayerProp("custom_options"),
      show: checkLayerProp("show"),
    };
  }

  get mapboxIndex(): string {
    return getDrawLayers(this.scene.getMapboxMap())[0];
  }

  removeLayer(): void {
    const map = this.scene.getMapboxMap();
    removeLayerGroup(map, getDrawLayers(map));
    removeSource(map, getDrawLayers(map));
    if (this.awaitingMapLoad) {
      map.off("render", this.attachWhenMapLoaded);
      this.awaitingMapLoad = false;
    } else {
      //@ts-ignore
      map.removeControl(this.mapboxDraw);
    }
    map.off("draw.modechange", this.handleModeChange);
    this.subscription.unsubscribe();
  }

  fetchData(_: DateTime): VoidAsyncResult {
    return createResolvedAsyncResult(undefined);
  }

  isRebuffering(_: DateTime): boolean {
    return false;
  }

  getActiveWeatherParametersAsString() {
    return [];
  }
}
